import React, { useEffect, useRef, useState } from 'react';
import { askDispatches, type AskResult } from '../../utils/askDispatches';

/**
 * YOUR PORTAL EMAIL ASSISTANT — the panel at the top of Dispatches.
 * 25 Sep 2026.
 *
 * The first version did two things: typing filtered the rows, and a second
 * press asked a real question. Tom killed the filter — *"I don't really need a
 * filter. It's the ask a question to help that we need"* — then the shortcuts
 * — *"as simple as possible. No / to jump to a box etc"* — and then said the
 * line that settles every future argument about this screen:
 *
 *   **"I don't want to train people to use this."**
 *
 * So: one box, one button, one answer. No keyboard shortcuts, no match counts,
 * no second mode, nothing to explain to Shilpa on her first morning. Anything
 * added here later has to survive that sentence — which is why there is no
 * mailbox picker (it reads both), no thread-count slider, and no cost readout.
 * The spend lives in the Anthropic console, where money belongs; putting a
 * price on the screen would teach everyone to hesitate before asking, and a
 * question nobody asks is the only truly wasteful one.
 *
 * A NAME AND AN EXAMPLE. NOTHING ELSE.
 *
 * The box first read "Ask a question about your email…", which names the tool
 * but shows nothing — read that and you assume search, type "Marjan", and
 * never discover you could have asked "what did we agree with Tikit and what
 * are we waiting on from them?" and got a dated answer with sources. Tom:
 * *"Can we think of a better way of explaining this?"* The next attempt put a
 * bare question in an empty field and drew *"Right idea but looks weird"* —
 * because a question sitting alone in a box reads as content somebody already
 * typed, not as an invitation.
 *
 * Tom's fix, and it is better than either: **name the thing, then show one
 * example marked as an example.** He tried four names in a minute — Email
 * Assistant, Portal Email Assistant, The Portal Email Assistant — and landed
 * on *Your*. Worth noticing why it is the right one: this reads the mailbox of
 * whoever is logged in, so Zeena asking it gets Zeena's answer. "Your" is the
 * only word of the four that is also true.
 *
 * "Eg." does the rest: it says the line is a suggestion, not a saved search.
 * There is no help text and no standfirst — he deleted one of those the same
 * afternoon (*"remove this — it's needless"*), and the rule it left behind
 * holds here: **if this box ever needs a sentence to explain it, the box is
 * wrong, not the reader.**
 *
 * Bloomberg because they are the busiest thing in the mailbox, so the example
 * is never hypothetical. If they go quiet, change the name, not the shape. And
 * the wording of the example is Tom's own — leave it alone.
 *
 * WHY IT IS A PANEL, AND WHY THE FIELD IS FULL WIDTH.
 *
 * The name spent an hour as a 10px tracked-out label — the same treatment as
 * "OPEN" and "CHASING" — which filed the cleverest thing in Portal OS under
 * furniture. Tom: *"it's an exciting feature, not something to hide away."* So
 * it is set in Jost at display size, and the whole thing sits in the bordered
 * panel with the paper left edge that he picked out on the rows — *"we can put
 * both in the grey style box — that looks stylish"*. On the rows that panel is
 * decoration, because everything wears it. Up here it is doing real work: it
 * makes the assistant an object on the page rather than a gap between the
 * title and the counts.
 *
 * The field runs the full width of that panel. It was briefly capped at 34rem
 * to stop the Ask button stranding at the far right; Tom: *"I don't want it
 * narrow — put it back how it was."* The panel solves the stranding anyway, by
 * giving the button an edge to sit against.
 *
 * THE WAIT IS THE OTHER DESIGN PROBLEM. This takes twenty to thirty seconds,
 * because it reads whole threads rather than skimming subject lines — which is
 * the point: the answer to Tom's test question was one sentence buried in a
 * quoted chain, in a thread that was neither the newest nor the most
 * promising-looking. Twenty-five silent seconds reads as broken, so the
 * elapsed count runs visibly. It is not decoration; it is the difference
 * between waiting and pressing Ask again.
 */

/** Bold and bullets, and nothing else. The model is told to write plainly. */
function inline(text: string): React.ReactNode[] {
  return text.split(/\*\*(.+?)\*\*/g).map((part, index) => index % 2 === 1 ? <strong key={index} className="font-semibold text-paper" data-id="element-947" data-mp="32fizrbmv7q86">
        {part}
      </strong> : <React.Fragment key={index} data-id="element-948" data-mp="37em5yzel02hq">{part}</React.Fragment>);
}
function Answer({
  text
}: {
  text: string;
}) {
  const lines = text.split('\n').map(line => line.trim());
  const blocks: React.ReactNode[] = [];
  let bullets: string[] = [];
  function flush() {
    if (!bullets.length) return;
    blocks.push(<ul key={`ul-${blocks.length}`} className="mt-3 space-y-2.5" data-id="element-949" data-mp="qpgptue3nsda">
        {bullets.map((item, index) => <li key={index} className="flex gap-3" data-id="element-950" data-mp="2b794k32s3xvz" data-mpi={index}>
            <span aria-hidden className="mt-[0.55em] h-px w-3 shrink-0 bg-paper/35" data-id="element-951" data-mp="276xhykquvnhv" />
            <span data-id="element-952" data-mp="279beky16p7rj">{inline(item)}</span>
          </li>)}
      </ul>);
    bullets = [];
  }
  for (const line of lines) {
    if (!line) {
      flush();
      continue;
    }
    if (/^[-•*]\s+/.test(line)) {
      bullets.push(line.replace(/^[-•*]\s+/, ''));
      continue;
    }
    flush();
    blocks.push(<p key={`p-${blocks.length}`} className="mt-3 first:mt-0" data-id="element-953" data-mp="1nj7zx58q11lu">
        {inline(line)}
      </p>);
  }
  flush();
  return <div className="max-w-[68ch] text-[0.975rem] leading-relaxed text-paper/75" data-id="element-954" data-mp="1jgl5qxokv4lh">{blocks}</div>;
}
export function DispatchSearch() {
  const [question, setQuestion] = useState('');
  const [asking, setAsking] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [result, setResult] = useState<AskResult | null>(null);
  const [failure, setFailure] = useState('');

  // A request in flight when the tab unmounts must not set state on the way
  // back. Dispatches refreshes itself on focus, so this happens in normal use.
  const live = useRef(true);
  useEffect(() => {
    live.current = true;
    return () => {
      live.current = false;
    };
  }, []);
  useEffect(() => {
    if (!asking) return;
    const started = Date.now();
    setElapsed(0);
    const timer = window.setInterval(() => {
      setElapsed(Math.round((Date.now() - started) / 1000));
    }, 1000);
    return () => window.clearInterval(timer);
  }, [asking]);
  async function submit(event: React.FormEvent) {
    event.preventDefault();
    const asked = question.trim();
    if (!asked || asking) return;
    setAsking(true);
    setResult(null);
    setFailure('');
    try {
      const answer = await askDispatches(asked);
      if (live.current) setResult(answer);
    } catch (error) {
      if (live.current) {
        setFailure(error instanceof Error ? error.message : 'That did not work.');
      }
    } finally {
      if (live.current) setAsking(false);
    }
  }
  return <div className="mt-12 border border-l-2 border-ruleDark/60 border-l-paper bg-paper/[0.04] px-5 py-6 sm:px-8 sm:py-8" data-id="element-955" data-mp="2is1jq5cq530n">
      <h2 className="font-display text-[1.4rem] font-light leading-none tracking-tight text-paper sm:text-[2rem]" data-id="element-956" data-mp="2wsc9f52asbk8">
        Your Portal Email Assistant
      </h2>

      <form onSubmit={submit} className="mt-6 flex items-center gap-4 border-b border-paper/25 pb-3 sm:mt-7" data-id="element-957" data-mp="2rqn1hyadge0d">
        <input type="text" value={question} onChange={event => setQuestion(event.target.value)} disabled={asking} placeholder="Eg. What did we last send Bloomberg?" aria-label="Ask a question about your email" className="min-w-0 flex-1 border-0 bg-transparent p-0 text-[1.0625rem] text-paper placeholder:text-paper/30 focus:outline-none disabled:text-paper/40 sm:text-[1.15rem]" data-id="element-958" data-mp="2023avn13r5vy" />
        <button type="submit" disabled={asking || !question.trim()} className="label inline-flex h-10 shrink-0 items-center border border-paper/30 px-5 text-paper/70 transition-colors duration-150 ease-out hover:border-paper hover:text-paper disabled:cursor-default disabled:border-paper/15 disabled:text-paper/30" data-id="element-959" data-mp="1gbhsydnk7l8u">
          {asking ? 'Asking' : 'Ask'}
        </button>
      </form>

      <div aria-live="polite" data-id="element-960" data-mp="1eqv1a11no49h">
        {asking && <p className="mt-4 text-[0.95rem] text-paper/45" data-id="element-961" data-mp="1eq3enfq1dgvc">
            Reading your email… {elapsed}s
            {elapsed >= 12 && <span className="text-paper/30" data-id="element-962" data-mp="1iq3wzbg9ac7o"> · long threads take a moment</span>}
          </p>}

        {failure && !asking && <p className="mt-4 max-w-[60ch] text-[0.95rem] leading-relaxed text-paper/60" data-id="element-963" data-mp="1sijey8rjz0sk">
            {failure}
          </p>}

        {result && !asking && <div className="mt-6" data-id="element-964" data-mp="1xf9tusrmfqce">
            <Answer text={result.answer} data-id="element-965" data-mp="22feb4l0csp3o" />

            {result.truncated && <p className="mt-4 max-w-[60ch] text-[0.875rem] leading-relaxed text-paper/40" data-id="element-966" data-mp="3mxylu5mjkud6">
                This answer was cut off before it finished. Ask again, more narrowly.
              </p>}

            {result.sources.length > 0 && <div className="mt-7 border-t border-ruleDark pt-5" data-id="element-967" data-mp="3d0amt3hwl0p7">
                <p className="label text-[0.7rem] text-paper/35" data-id="element-968" data-mp="3820qgpkfzu20">
                  Read {result.threadsRead}{' '}
                  {result.threadsRead === 1 ? 'conversation' : 'conversations'}
                </p>
                <ul className="mt-3 space-y-2" data-id="element-969" data-mp="4kqymiw3a8h0">
                  {result.sources.map(source => <li key={source.n} className="flex gap-3 text-[0.9rem] leading-snug" data-id="element-970" data-mp="3vxbwh1mejozh">
                      <span className="w-4 shrink-0 tabular-nums text-paper/30" data-id="element-971" data-mp="3qz1zqml14gbr">{source.n}</span>
                      <a href={source.url} target="_blank" rel="noreferrer" className="text-paper/55 underline decoration-paper/20 underline-offset-4 transition-colors duration-150 ease-out hover:text-paper hover:decoration-paper/50" data-id="element-972" data-mp="sal8qsp0xixk">
                        {source.subject}
                      </a>
                      <span className="shrink-0 text-paper/25" data-id="element-973" data-mp="1hdlwxsq7sx78">
                        {source.messages} {source.messages === 1 ? 'message' : 'messages'}
                      </span>
                    </li>)}
                </ul>
              </div>}
          </div>}
      </div>
    </div>;
}